import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { CompanyData, getCompanyData } from '~/data/company';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useSWR } from '~/hooks/useSWR';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';
import { hasOneOfRoles } from '~/services/Permissions';

export interface UseTopbarOutput {
  employeeId: number;
  loggedInEmployeeId: number;
  canAdminister: boolean;
  canAddEmployee: boolean;
  canAddEmployeeCandidate: boolean;
  logoId: string | undefined;
  companyData?: CompanyData;
  companyDataLoading: boolean;
  companyDataFailedToLoad: boolean;
  newEmployeePage: string;
}

export const useTopbar = (): UseTopbarOutput => {
  const { applicationConfig } = useApplicationConfig();
  const managerCanCreateEmployee =
    applicationConfig?.fComConfig?.mgrConfig?.fComEnableMgrCreateEmployee ??
    false;

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const {
    selectedEmployeeId,
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const userRoles = useLoggedInUserRoles();

  const hasAccessToEmployeeModule =
    hasRoleAccessOverEmployee(employeeModulesAccessRoles.people.all) ||
    isSelectedEmployeeLoggedInUser;

  const canAdminister = hasOneOfRoles(userRoles, [
    Roles.HR_ADMIN,
    Roles.HR_ADMIN_LIMITED,
    Roles.HR_STAFF,
    Roles.HR_DEV,
  ]);

  const canAddEmployeeCandidate = !managerCanCreateEmployee
    ? hasOneOfRoles(userRoles, [Roles.MANAGER])
    : false;
  const canAddEmployee = hasOneOfRoles(userRoles, [
    Roles.HR_ADMIN,
    Roles.HR_ADMIN_LIMITED,
    Roles.RECRUITER,
    Roles.HR_STAFF,
    Roles.HR_SALARY,
    ...(managerCanCreateEmployee ? [Roles.MANAGER] : []),
  ]);

  const {
    data: companyData,
    loading: companyDataLoading,
    failedToLoad: companyDataFailedToLoad,
  } = useSWR('getCompanyData', getCompanyData);

  const getLogoId = () => {
    if (!companyData || !companyData.logo.fComLogoId) {
      return 'dfltLogo';
    }

    return companyData.logo.fComLogoId;
  };

  const logoId = getLogoId();

  const newEmployeePage =
    process.env.REACT_APP_ENABLE_EMPLOYMENT_TEMPLATES === 'yes'
      ? 'page-newEmpFormTemplate.html'
      : 'page-newEmp.html';

  return {
    companyData,
    employeeId: hasAccessToEmployeeModule
      ? selectedEmployeeId
      : loggedInEmployeeId,
    loggedInEmployeeId,
    canAdminister,
    canAddEmployee,
    canAddEmployeeCandidate,
    logoId,
    companyDataLoading,
    companyDataFailedToLoad,
    newEmployeePage,
  };
};
